import { gql } from "@apollo/client";

// GET_STORIES query (limit, page, latestOnly parameters)
export const GET_STORIES = gql`
  query GetStories($limit: Int, $page: Int, $latestOnly: Boolean) {
    stories(limit: $limit, page: $page, latestOnly: $latestOnly) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      views
      likeCount
      bookmarkCount
      totalChapterLikes
      totalChapterComments
      commentCount
      totalComments
      chapterCount
      publishedChapterCount
      totalWordCount
      status
      timestamp
      modifiedAt
      type
      isDefaultImage
    }
  }
`;

// GET_STORY query (id parameter)
export const GET_STORY = gql`
  query GetStory($id: ID!) {
    story(id: $id) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      views
      likeCount
      bookmarkCount
      totalChapterLikes
      totalChapterComments
      commentCount
      totalComments
      chapterCount
      publishedChapterCount
      totalWordCount
      status
      timestamp
      modifiedAt
      type
      isDefaultImage
    }
  }
`;

// GET_TRENDING_STORIES query (limit parameter)
export const GET_TRENDING_STORIES = gql`
  query GetTrendingStories($limit: Int) {
    trendingStories(limit: $limit) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      views
      likeCount
      bookmarkCount
      totalChapterLikes
      totalChapterComments
      commentCount
      totalComments
      chapterCount
      publishedChapterCount
      totalWordCount
      status
      timestamp
      modifiedAt
      type
      isDefaultImage
    }
  }
`;

// GET_POPULAR_STORIES query (limit parameter)
export const GET_POPULAR_STORIES = gql`
  query GetPopularStories($limit: Int) {
    mostPopularStories(limit: $limit) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      views
      likeCount
      bookmarkCount
      totalChapterLikes
      totalChapterComments
      commentCount
      totalComments
      chapterCount
      publishedChapterCount
      totalWordCount
      status
      timestamp
      modifiedAt
      type
      isDefaultImage
    }
  }
`;

// GET_TOP_STORIES_BY_CATEGORY query (category, limit parameters)
export const GET_TOP_STORIES_BY_CATEGORY = gql`
  query GetTopStoriesByCategory($category: StoryCategory!, $limit: Int) {
    topStoriesByCategory(category: $category, limit: $limit) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      views
      likeCount
      bookmarkCount
      totalChapterLikes
      totalChapterComments
      commentCount
      totalComments
      chapterCount
      publishedChapterCount
      totalWordCount
      status
      timestamp
      modifiedAt
      type
      isDefaultImage
    }
  }
`;

// GET_TOP_STORIES_BY_CATEGORIES query (categories array, limit parameters) - optimized batch query
export const GET_TOP_STORIES_BY_CATEGORIES = gql`
  query GetTopStoriesByCategories($categories: [StoryCategory!]!, $limit: Int) {
    topStoriesByCategories(categories: $categories, limit: $limit) {
      category
      stories {
        id
        title
        description
        subtitle
        author
        userId
        mainCharacters
        category
        language
        copyright
        tags
        targetAudience
        mature
        coverPhoto
        imgUrl
        views
        likeCount
        bookmarkCount
        totalChapterLikes
        totalChapterComments
        commentCount
        totalComments
        chapterCount
        publishedChapterCount
        totalWordCount
        status
        timestamp
        modifiedAt
        type
        isDefaultImage
      }
    }
  }
`;

// GET_STORIES_BY_PAGE query (all filter parameters)
export const GET_STORIES_BY_PAGE = gql`
  query GetStoriesByPage(
    $page: Int
    $pageSize: Int
    $category: StoryCategory
    $author: String
    $type: StoryType
    $search: String
    $sortOrder: String
    $userId: String
    $status: StoryStatus
  ) {
    storiesByPage(
      page: $page
      pageSize: $pageSize
      category: $category
      author: $author
      type: $type
      search: $search
      sortOrder: $sortOrder
      userId: $userId
      status: $status
    ) {
      stories {
        id
        title
        description
        subtitle
        author
        userId
        mainCharacters
        category
        language
        copyright
        tags
        targetAudience
        mature
        coverPhoto
        imgUrl
        views
        likeCount
        bookmarkCount
        totalChapterLikes
        totalChapterComments
        commentCount
        totalComments
        chapterCount
        publishedChapterCount
        totalWordCount
        status
        timestamp
        modifiedAt
        type
        isDefaultImage
      }
      currentPage
      totalPages
    }
  }
`;

// GET_MY_STORIES_BY_PAGE query (page, pageSize, type parameters)
export const GET_MY_STORIES_BY_PAGE = gql`
  query GetMyStoriesByPage($page: Int, $pageSize: Int, $type: StoryType) {
    myStoriesByPage(page: $page, pageSize: $pageSize, type: $type) {
      stories {
        id
        title
        description
        subtitle
        author
        userId
        mainCharacters
        category
        language
        copyright
        tags
        targetAudience
        mature
        coverPhoto
        imgUrl
        views
        likeCount
        bookmarkCount
        totalChapterLikes
        totalChapterComments
        commentCount
        totalComments
        chapterCount
        publishedChapterCount
        totalWordCount
        status
        timestamp
        modifiedAt
        type
        isDefaultImage
      }
      currentPage
      totalPages
    }
  }
`;

// GET_MY_BOOKMARKED_STORIES query (limit, page parameters)
export const GET_MY_BOOKMARKED_STORIES = gql`
  query GetMyBookmarkedStories($limit: Int, $page: Int) {
    myBookmarkedStories(limit: $limit, page: $page) {
      stories {
        id
        title
        description
        subtitle
        author
        userId
        mainCharacters
        category
        language
        copyright
        tags
        targetAudience
        mature
        coverPhoto
        imgUrl
        views
        likeCount
        bookmarkCount
        totalChapterLikes
        commentCount
        chapterCount
        publishedChapterCount
        totalWordCount
        status
        timestamp
        modifiedAt
        type
        isDefaultImage
      }
      currentPage
      totalPages
    }
  }
`;

// BOOKMARK_STORY mutation (storyId parameter)
export const BOOKMARK_STORY = gql`
  mutation BookmarkStory($storyId: ID!) {
    bookmarkStory(storyId: $storyId)
  }
`;

// UNBOOKMARK_STORY mutation (storyId parameter)
export const UNBOOKMARK_STORY = gql`
  mutation UnbookmarkStory($storyId: ID!) {
    unbookmarkStory(storyId: $storyId)
  }
`;

// CREATE_STORY mutation (data parameter)
export const CREATE_STORY = gql`
  mutation CreateStory($data: StoryInput!) {
    createStory(data: $data) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      status
      timestamp
      isDefaultImage
    }
  }
`;

// UPDATE_STORY mutation (id, data parameters)
export const UPDATE_STORY = gql`
  mutation UpdateStory($id: ID!, $data: StoryInput!) {
    updateStory(id: $id, data: $data) {
      id
      title
      description
      subtitle
      author
      userId
      mainCharacters
      category
      language
      copyright
      tags
      targetAudience
      mature
      coverPhoto
      imgUrl
      status
      modifiedAt
      isDefaultImage
    }
  }
`;

// DELETE_STORY mutation (id parameter)
export const DELETE_STORY = gql`
  mutation DeleteStory($id: ID!) {
    deleteStory(id: $id)
  }
`;

// GET_MY_STORY_STATS query (no parameters - returns counts only)
export const GET_MY_STORY_STATS = gql`
  query GetMyStoryStats {
    myStoryStats {
      totalStories
      oneShotCount
      seriesCount
    }
  }
`;

